import * as React from "react";

import useAuthorized from "../hooks/useAuthorized";
import type { IUserPayload } from "../PermissionContext";

export interface HasAccessProps<
  Role extends string = string,
  Permission extends string = string
> {
  roles?: Role[] | Role;
  permissions?: Permission[] | Permission;
  /** Require every listed role AND permission instead of ANY. */
  requireAll?: boolean;
  /** Custom predicate for rules roles/permissions can't express. */
  check?: (user: IUserPayload) => boolean;
  /** Rendered when the user is NOT authorized. */
  renderAuthFailed?: React.ReactNode;
  /**
   * @deprecated Checks are now synchronous, so there is no loading phase.
   * Kept for backwards compatibility; it is ignored.
   */
  isLoading?: React.ReactNode;
  /**
   * Content shown when authorized. May also be a render-prop function that
   * receives the boolean result, letting you render both states inline.
   */
  children?: React.ReactNode | ((isAllowed: boolean) => React.ReactNode);
}

const AllowedAccess = <
  Role extends string = string,
  Permission extends string = string
>({
  roles,
  permissions,
  requireAll,
  check,
  renderAuthFailed = null,
  children,
}: HasAccessProps<Role, Permission>): React.ReactElement | null => {
  const isAllowed = useAuthorized<Role, Permission>({
    roles,
    permissions,
    requireAll,
    check,
  });

  if (typeof children === "function") {
    return <>{children(isAllowed)}</>;
  }

  if (isAllowed) {
    return <>{children}</>;
  }

  return <>{renderAuthFailed}</>;
};

export default AllowedAccess;
